import React from 'react';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import { Switch,AppBar, createStyles, FormControl, FormControlLabel, Grid, MenuItem, Select, Theme, Toolbar, Typography, withStyles, WithStyles, Fade } from '@material-ui/core';
import { ErrorData, HostType, LicenseType, validateHostId, initializeHostIdHelperText } from './ActivateLicenseData';
import { LinesData } from './DealsContext';
import { RawActivationData } from './HistoryDialog';
import { DeltaXMLColors } from './styling/DeltaColors';
import EulaCheckBox, { EulaAgreedPreviously } from './EulaCheckBox';
import { DTFormatter } from '../general/datetime-formatter/DTFormatter';

const styles = (theme: Theme) =>
  createStyles({
    appBar: {
      alignItems: "left",
      backgroundColor: DeltaXMLColors.OXFORD_BLUE,
    },
    textField: {
      backgroundColor: DeltaXMLColors.CONCRETE,
      margin: theme.spacing(1),
      minwidth: 125,
      paddingStart: '5px',
      paddingEnd: '5px'
    },
    hostIdTextField: {
      margin: theme.spacing(1),
      minWidth: 260,
    },
    hostForm: {
      backgroundColor: DeltaXMLColors.CONCRETE,
      borderRadius: 5,
      margin: theme.spacing(1),
      minWidth: 125,
    },
    select: {
      marginLeft: '5px',
      marginRight: '5px'
    }
  });

export interface SystemActivationData {
  hostType: HostType ,
  hostType2: HostType,
  hostType3: HostType,
  hostId: string,
  hostId2: string,
  hostId3: string,
  port?: string,
  returnCount?: number,
  rawActivations?: RawActivationData[],
  licenseId?: string
}

export interface ActivateLicenseProps extends WithStyles<typeof styles> {
  dealId: string,
  line: LinesData,
  dialogOpen: boolean;
  handleCloseDialog: () => void;
  handleActivation: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, data: SystemActivationData) => void;
  dateFormat: string;
}

export interface EulaCheckBoxData {
  previouslyAgreed: EulaAgreedPreviously;
  isCustom: boolean;
}
export interface HostData {
  row: HostDataRow,
  value?: string
}

export enum HostDataRow {
  none,
  hostRow1,
  hostRow2,
  hostRow3
}

function ActivateLicenseDialog(props: ActivateLicenseProps) {
  const { classes, dealId, line, dialogOpen, handleCloseDialog, handleActivation, dateFormat } = props;
  const [hostType, setHostType] = React.useState((line.allowedHostIds.length !== 1 ? "" : line.allowedHostIds[0]) as HostType);
  const [hostType2, setHostType2] = React.useState((line.allowedHostIds.length !== 1 ? "" : line.allowedHostIds[0]) as HostType);
  const [hostType3, setHostType3] = React.useState((line.allowedHostIds.length !== 1 ? "" : line.allowedHostIds[0]) as HostType);
  const [hostId, setHostId] = React.useState('');
  const [hostId2, setHostId2] = React.useState('');
  const [hostId3, setHostId3] = React.useState('');
  const [portNumber, setPortNumber] = React.useState('');
  const [isEulaAccepted, setEulaAccepted] = React.useState(false);
  const [displayedLicenseId, setDisplayedLicenseId] = React.useState('');
  const [eulaData, setEulaData] = React.useState<EulaCheckBoxData>({previouslyAgreed: EulaAgreedPreviously.unknown, isCustom: false})

  const [disableThreeServerRedundancy, setcheckboxThreeServer] = React.useState(true);

  const licenseType: LicenseType = line.licenseModel as LicenseType;
  const lineSupportExpiry: string = DTFormatter.getFormattedDate(line.supportExpiry, dateFormat); 
  const requirePortEntry = licenseType === LicenseType.fixed_host_rest;
  const triadCompatible = line.allowedHostIds.includes(HostType.internetx3) || line.allowedHostIds.includes(HostType.ethernetx3) || line.allowedHostIds.includes(HostType.hostNamex3);

  const initialErrorData: ErrorData = {
    hostId: (licenseType !== LicenseType.server_worldbox && licenseType !== LicenseType.server_boeing && licenseType !== LicenseType.server_unlocked) ? initializeHostIdHelperText(hostType) : null,
    hostId2: (licenseType !== LicenseType.server_worldbox && licenseType !== LicenseType.server_boeing && licenseType !== LicenseType.server_unlocked) ? initializeHostIdHelperText(hostType2) : null,
    hostId3: (licenseType !== LicenseType.server_worldbox && licenseType !== LicenseType.server_boeing && licenseType !== LicenseType.server_unlocked) ? initializeHostIdHelperText(hostType3) : null,
    port: requirePortEntry ? "Enter Port Number" : null
  }
  
  const [errorData, setErrorData] = React.useState(initialErrorData);
  let isFormValid: boolean = fieldsWithNoErrors(errorData, disableThreeServerRedundancy);

  const handleHostTypeChange = (hostTypeArg: HostType) => {
    // as a convenience (because in most cases all host types will be the same)
    // if the first host type is changed, change all other host types:
    setHostType(hostTypeArg);
    setHostType2(hostTypeArg);
    setHostType3(hostTypeArg);
    setHostId('');
    setHostId2('');
    setHostId3('');
    const errMessage = initializeHostIdHelperText(hostTypeArg);
    setErrorData({ ...errorData, 
      hostId: errMessage,
      hostId2: errMessage,
      hostId3: errMessage
    });
  };

  const handleEulaDataRequest = (data: EulaCheckBoxData) => {
    setEulaData(data);
  }

  const handleHostType2Change = (hostTypeArg: HostType) => {
    setHostType2(hostTypeArg);
    setHostId2('');
    validateCurrentHostIds(!disableThreeServerRedundancy, {row: HostDataRow.hostRow2, value: hostTypeArg}, [hostId, '', hostId3]);
  };

  const handleHostType3Change = (hostTypeArg: HostType) => {
    setHostType3(hostTypeArg);
    setHostId3('');
    validateCurrentHostIds(!disableThreeServerRedundancy, {row: HostDataRow.hostRow3, value: hostTypeArg}, [hostId, hostId2, '']); 
  };

  const handleHostIdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateHostId(event.target.value);
  }

  const handleHostId2Change = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateHostId2(event.target.value);
  }

  const handleHostId3Change = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateHostId3(event.target.value);
  }

  const updateHostId = (hostIdArg: string) => {
    setHostId(hostIdArg);
    validateCurrentHostIds(!disableThreeServerRedundancy, {row: HostDataRow.none}, [hostIdArg, hostId2, hostId3]);
  }

  const updateHostId2 = (hostId2Arg: string) => {
    setHostId2(hostId2Arg);
    validateCurrentHostIds(!disableThreeServerRedundancy, {row: HostDataRow.none}, [hostId, hostId2Arg, hostId3]);

  }

  const updateHostId3 = (hostId3Arg: string) => {
    setHostId3(hostId3Arg);
    validateCurrentHostIds(!disableThreeServerRedundancy, {row: HostDataRow.none}, [hostId, hostId2, hostId3Arg]);
  }

  const validateCurrentHostIds = (checkDuplicates: boolean, hostData: HostData, allHostIds = [hostId, hostId2, hostId3]) => {
    const ht1 = (hostData.row === HostDataRow.hostRow1? hostData.value : hostType) as HostType;
    const ht2 = (hostData.row === HostDataRow.hostRow2? hostData.value : hostType2) as HostType;
    const ht3 = (hostData.row === HostDataRow.hostRow3? hostData.value : hostType3) as HostType;

    const errorMsg1 = validateHostId(ht1, allHostIds[0], allHostIds, checkDuplicates);
    const errorMsg2 = validateHostId(ht2, allHostIds[1], allHostIds, checkDuplicates);
    const errorMsg3 = validateHostId(ht3, allHostIds[2], allHostIds, checkDuplicates);

    setErrorData(prevErrData => {
      const newErrData = { ...prevErrData };
      newErrData["hostId"] = errorMsg1;
      newErrData["hostId2"] = errorMsg2;
      newErrData["hostId3"] = errorMsg3;
      return newErrData;     
    });
  }

  const handlePortNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPortNumber(event.target.value);
    if (event.target.value === "") {
      setErrorData({ ...errorData, port: 'Enter Port Number' })
    } else {
      const updatedPortNumber: number = Number(event.target.value);
      if (isNaN(updatedPortNumber)) {
        setErrorData({ ...errorData, port: 'Invalid Port Number' })
      } else if (!isNaN(updatedPortNumber) && (updatedPortNumber < 0 || updatedPortNumber > 65535)) {
        setErrorData({ ...errorData, port: 'Port Number out of range' });
      } else {
        setErrorData({ ...errorData, port: null });
      }
    }
  }

  const onActivateClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    let hostObject: SystemActivationData;
    if(disableThreeServerRedundancy) {
       hostObject = { hostType: hostType, hostType2: HostType.na, hostType3: HostType.na, hostId: hostId, hostId2: "", hostId3: "", licenseId: displayedLicenseId} ;
    } else {
      hostObject = { hostType: hostType, hostType2: hostType2, hostType3: hostType3, hostId: hostId, hostId2: hostId2, hostId3: hostId3, licenseId: displayedLicenseId} ;
    }
    setEulaAccepted(false);
    handleActivation(event, portNumber ? {...hostObject, port: portNumber} : hostObject);
  }

  const handleEulaCheckboxChange = () => {
    setEulaAccepted(!isEulaAccepted)
  }

  const handleLicenseAccepted = (licenseId: string) => {
    setDisplayedLicenseId(licenseId);
  }

  const handleThreeServer = (event:any) => {
    setcheckboxThreeServer(!disableThreeServerRedundancy);
    validateCurrentHostIds(disableThreeServerRedundancy, {row: HostDataRow.none});
  }

  const noHostIdsRequired = line.allowedHostIds.length === 0;
  const treatAsIfEulaAccepted = (isEulaAccepted || eulaData.isCustom || eulaData.previouslyAgreed === EulaAgreedPreviously.yes);

  return (
    <React.Fragment>
      <Dialog open={dialogOpen} onClose={handleCloseDialog}>
        <AppBar className={classes.appBar} position="static">
          <Toolbar>
            <Typography variant="h4" >Activate License</Typography>
          </Toolbar>
        </AppBar>
        <br />
        {noHostIdsRequired ?
          <DialogContent>
            <Typography variant="body1" color="textSecondary">
              Click on confirm button to activate license
            </Typography>
            <br/>
            <EulaCheckBox dealId={dealId} eulaRequestData={eulaData} lineLicenseType={licenseType} lineExpiry={lineSupportExpiry} isEulaAccepted={isEulaAccepted} handleEulaCheckboxChange={handleEulaCheckboxChange} handleLicenseAccepted={handleLicenseAccepted} handleEulaDataRequest={handleEulaDataRequest}/>
          </DialogContent>
          :
          <DialogContent>
            <Typography variant="h6">
              Configure Hosts
            </Typography>
            {(triadCompatible) && <FormControlLabel control={<Switch checked={!disableThreeServerRedundancy} />} onChange={handleThreeServer} label="Three-Server Redundancy" />}
            <br />
            <DialogContentText>
              <Typography variant="body1" color="textSecondary">
                Select the license host type and enter an appropriate value
              </Typography>
            </DialogContentText>
            <Grid container>
              <Grid item xs={4}>
                <FormControlLabel
                  control={
                    <FormControl className={classes.hostForm}>
                      <Select className={classes.select} value={hostType} onChange={event => handleHostTypeChange(event.target.value as HostType)} displayEmpty>
                        {line.allowedHostIds.length > 1 &&
                          <MenuItem value="" disabled>Select host type</MenuItem>
                        }
                        {line.allowedHostIds?.filter(type => type !== HostType.ethernetx3)
                          .filter(type => type !== HostType.hostNamex3)
                          .filter(type => type !== HostType.internetx3)
                          .map(type =>
                          <MenuItem key={type} value={type} style={{color: DeltaXMLColors.OXFORD_BLUE}}>{type}</MenuItem>
                        )}
                      </Select>
                    </FormControl>
                  }
                  label="Host Type"
                  labelPlacement="top"
                />
              </Grid>
              <Grid item xs={8}>
                <FormControlLabel
                  control={<TextField className={classes.hostIdTextField} value={hostId} onChange={handleHostIdChange}
                    helperText={errorData.hostId !== null ? errorData.hostId : "Valid Host Id"} error={errorData.hostId !== null} />}
                  label="Host Id"
                  labelPlacement="top"
                />
              </Grid>
            </Grid>
            <Grid container>
              {(requirePortEntry) &&
                <Grid item xs={12}>
                  <FormControlLabel
                    control={<TextField className={classes.textField} value={portNumber} onChange={handlePortNumberChange}
                      helperText={errorData.port !== null ? errorData.port : "Valid Port Number"} error={errorData.port !== null} />}
                    label="Port"
                    labelPlacement="top"
                  />
                </Grid>}
            </Grid>
            {(triadCompatible) &&
              <Fade in={!disableThreeServerRedundancy}>
                <Grid container>
                  <Grid container>
                    <Grid item xs={4}>
                      <FormControlLabel
                        control={
                          <FormControl className={classes.hostForm}>
                            <Select className={classes.select}
                              disabled={disableThreeServerRedundancy}
                              value={hostType2} onChange={event => handleHostType2Change(event.target.value as HostType)} displayEmpty>
                              {line.allowedHostIds.length > 1 &&
                                <MenuItem value="" disabled>Select host type</MenuItem>
                              }
                              {line.allowedHostIds?.filter(type => type !== HostType.ethernetx3)
                                .filter(type => type !== HostType.hostNamex3)
                                .filter(type => type !== HostType.internetx3)
                                .map(type =>
                                  <MenuItem key={type} value={type} style={{ color: DeltaXMLColors.OXFORD_BLUE }}>{type}</MenuItem>
                                )}
                            </Select>
                          </FormControl>
                        }
                        label="Host Type"
                        labelPlacement="top"
                      />
                    </Grid>
                    <Grid item xs={8}>
                      <FormControlLabel
                        control={<TextField className={classes.hostIdTextField} value={hostId2} onChange={handleHostId2Change}
                          helperText={errorData.hostId2 !== null ? errorData.hostId2 : "Valid Host Id"} error={errorData.hostId2 !== null}
                          disabled={disableThreeServerRedundancy} />}
                        label="Secondary Host Id"
                        labelPlacement="top"
                      />
                    </Grid>
                  </Grid>
                  <Grid container>
                    <Grid item xs={4}>
                      <FormControlLabel
                        control={
                          <FormControl className={classes.hostForm}>
                            <Select className={classes.select} value={hostType3}
                              disabled={disableThreeServerRedundancy}
                              onChange={event => handleHostType3Change(event.target.value as HostType)} displayEmpty>
                              {line.allowedHostIds.length > 1 &&
                                <MenuItem value="" disabled>Select host type</MenuItem>
                              }
                              {line.allowedHostIds?.filter(type => type !== HostType.ethernetx3)
                                .filter(type => type !== HostType.hostNamex3)
                                .filter(type => type !== HostType.internetx3)
                                .map(type =>
                                  <MenuItem key={type} value={type} style={{ color: DeltaXMLColors.OXFORD_BLUE }}>{type}</MenuItem>
                                )}
                            </Select>
                          </FormControl>
                        }
                        label="Host Type"
                        labelPlacement="top"
                      />
                    </Grid>
                    <Grid item xs={8}>
                      <FormControlLabel
                        control={<TextField className={classes.hostIdTextField} value={hostId3} onChange={handleHostId3Change}
                          helperText={errorData.hostId3 !== null ? errorData.hostId3 : "Valid Host Id"} error={errorData.hostId3 !== null}
                          disabled={disableThreeServerRedundancy} />}
                        label="Tertiary Host Id"
                        labelPlacement="top"
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Fade>
            }
            <EulaCheckBox dealId={dealId} eulaRequestData={eulaData} lineLicenseType={licenseType} lineExpiry={lineSupportExpiry} isEulaAccepted={isEulaAccepted} handleEulaCheckboxChange={handleEulaCheckboxChange} handleLicenseAccepted={handleLicenseAccepted} handleEulaDataRequest={handleEulaDataRequest}/>
          </DialogContent>
        }
        <DialogActions>
          <Button onClick={event => onActivateClick(event)} variant="contained" color="primary" 
          disabled={!(isFormValid && treatAsIfEulaAccepted) || 
          (noHostIdsRequired && !treatAsIfEulaAccepted)}>
            Confirm
          </Button>
          <Button onClick={handleCloseDialog} variant="contained" color="primary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}
export default withStyles(styles)(ActivateLicenseDialog);

const fieldsWithNoErrors = (errorData: ErrorData, disableThreeServerRedundancy: boolean): boolean => {
  let fieldsWithNoErrors: boolean = false;
  let additionalHostIds = ["hostId2", "hostId3"];
  const otherError = Object.entries(errorData)
  .filter((property) => {
    const [name, value] = property;
    return disableThreeServerRedundancy ? !additionalHostIds.includes(name) : true ;
  })
  .find((property) => {
    const [name, value] = property;
    return value !== null;
  });
  
  if (!otherError) {
    fieldsWithNoErrors = true;
  }
  return fieldsWithNoErrors;
}